import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Home as HomeIcon, Joystick, Dices, ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";
import { useKeyboardMovement } from "@/hooks/useKeyboardMovement";
import { useIsTouch } from "@/hooks/useIsTouch";
import { Avatar } from "@/components/game/Avatar";
import { MoveHint } from "@/components/game/MoveHint";
import { InteractPrompt } from "@/components/game/InteractPrompt";
import { GameArcade } from "@/components/games/GameArcade";

const BOUNDS = { minX: 6, maxX: 94, minY: 30, maxY: 88 };
const THRESHOLD = 11;
const START = { x: 50, y: 84 };

const ZONES = [
  { id: "arcade", kind: "arcade", label: "Arcade Console", x: 34, y: 52 },
  { id: "board", kind: "board", label: "Board Games", x: 72, y: 56 },
  { id: "home", kind: "home", label: "Nostalgic World", x: 10, y: 82 },
];

export default function GameRoom() {
  const navigate = useNavigate();
  const [arcadeOpen, setArcadeOpen] = useState(false);
  const [boardOpen, setBoardOpen] = useState(false);
  const isTouch = useIsTouch();
  const anyOverlay = arcadeOpen || boardOpen;
  const { pos, setPos, facing, moving, moveTo } = useKeyboardMovement({ initial: START, bounds: BOUNDS, enabled: !anyOverlay });

  const tapToWalk = (e) => {
    if (anyOverlay) return;
    const r = e.currentTarget.getBoundingClientRect();
    moveTo(((e.clientX - r.left) / r.width) * 100, ((e.clientY - r.top) / r.height) * 100);
  };

  const active = useMemo(() => {
    let best = null, bestD = THRESHOLD;
    for (const z of ZONES) { const d = Math.hypot(z.x - pos.x, z.y - pos.y); if (d < bestD) { bestD = d; best = z; } }
    return best;
  }, [pos]);

  const interact = (z) => {
    if (!z) return;
    if (z.kind === "home") return navigate("/");
    if (z.kind === "arcade") return setArcadeOpen(true);
    if (z.kind === "board") return setBoardOpen(true);
  };

  useEffect(() => {
    const h = (e) => { if (e.key === "Enter" && !anyOverlay && active) { e.preventDefault(); interact(active); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyOverlay, active]);

  const holdRef = useRef(null);
  const nudge = (dx, dy) => setPos((p) => ({ x: Math.max(BOUNDS.minX, Math.min(BOUNDS.maxX, p.x + dx)), y: Math.max(BOUNDS.minY, Math.min(BOUNDS.maxY, p.y + dy)) }));
  const startHold = (dx, dy) => { if (anyOverlay) return; nudge(dx, dy); holdRef.current = setInterval(() => nudge(dx, dy), 40); };
  const stopHold = () => holdRef.current && clearInterval(holdRef.current);

  return (
    <div className="min-h-screen bg-background paper-grain">

      <header className="sticky top-0 z-40 border-b-[3px] border-kirana-brown bg-[#2B1055] text-kirana-cream">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-2.5">
          <div className="min-w-0">
            <h1 className="font-signboard text-xl font-bold leading-none sm:text-3xl">Game Room · गेम रूम</h1>
            <p className="text-[11px] text-kirana-cream/80">Pixel-art arcade & desi board games — walk up and press ⏎</p>
          </div>
          <Link data-testid="open-home" to="/" className="flex shrink-0 items-center gap-1.5 rounded-md border-2 border-kirana-cream bg-black/25 px-2.5 py-1 text-xs font-bold transition-colors hover:bg-black/40 sm:px-3 sm:py-1.5 sm:text-sm">
            <HomeIcon className="h-4 w-4" /> <span className="hidden sm:inline">Home</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-3 py-4">
        <div className="mb-2 flex items-center justify-end">
          <MoveHint verb="play" />
        </div>

        <div data-testid="gameroom-area" className="play-surface relative h-[70vh] min-h-[480px] w-full overflow-hidden rounded-xl border-[4px] border-kirana-brown hard-shadow">
          {/* parlour backdrop */}
          <div className="pointer-events-none absolute inset-0 z-10" style={{ background: "radial-gradient(circle at 50% 15%, #3b1470 0%, #1b0b3a 60%, #100826 100%)" }}>
            <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "linear-gradient(#a78bfa 1px, transparent 1px), linear-gradient(90deg, #a78bfa 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
            {/* checkered floor */}
            <div className="absolute inset-x-0 bottom-0 h-[38%]" style={{ backgroundImage: "linear-gradient(45deg, #1c0f3a 25%, transparent 25%, transparent 75%, #1c0f3a 75%), linear-gradient(45deg, #1c0f3a 25%, transparent 25%, transparent 75%, #1c0f3a 75%)", backgroundSize: "44px 44px", backgroundPosition: "0 0, 22px 22px", opacity: 0.5 }} />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent" />

            {/* marquee */}
            <div className="absolute left-1/2 top-[2.5%] -translate-x-1/2 rounded-md border-2 border-kirana-gold bg-[#7C3AED] px-4 py-1 text-center hard-shadow-sm">
              <span className="font-signboard text-xs font-bold tracking-wide text-kirana-cream sm:text-sm">✦ 90s VIDEO GAME PARLOUR ✦</span>
            </div>

            {/* pennant string */}
            <div className="absolute inset-x-6 top-[13%] flex justify-between">
              {Array.from({ length: 14 }).map((_, i) => (
                <span key={i} className="h-0 w-0 border-x-[6px] border-t-[10px] border-x-transparent" style={{ borderTopColor: ["#ff2d95", "#ffd60a", "#22d3ee", "#7CFC00"][i % 4] }} />
              ))}
            </div>

            {/* wall neon signs */}
            <div className="absolute left-[6%] top-[20%] rounded-md border-2 border-[#22d3ee] px-2 py-0.5" style={{ boxShadow: "0 0 10px #22d3ee66" }}>
              <span className="font-signboard text-[9px] font-bold text-[#22d3ee] sm:text-[11px]">★ HIGH SCORE ★</span>
            </div>
            <div className="absolute right-[6%] top-[20%] rounded-md border-2 border-[#ff2d95] px-2 py-0.5" style={{ boxShadow: "0 0 10px #ff2d9566" }}>
              <span className="font-signboard text-[9px] font-bold text-[#ff2d95] sm:text-[11px]">1 PLAYER</span>
            </div>

            {/* decorative claw / prize machine to fill the corner */}
            <div className="absolute right-[3%] top-[34%] hidden w-24 flex-col items-center sm:flex">
              <div className="w-full rounded-t-md border-[3px] border-black bg-[#c1121f] py-0.5 text-center"><span className="font-signboard text-[9px] font-bold text-white">PRIZES</span></div>
              <div className="h-28 w-full border-[3px] border-black bg-gradient-to-b from-[#ffb3c1]/30 to-[#7C3AED]/30 backdrop-blur-[1px]">
                <div className="mt-2 flex flex-wrap justify-center gap-1 px-1">
                  {["#ffd60a", "#22d3ee", "#7CFC00", "#ff2d95", "#ffd60a", "#22d3ee"].map((c, i) => (<span key={i} className="h-3 w-3 rounded-full" style={{ background: c }} />))}
                </div>
              </div>
              <div className="h-2 w-full bg-black" />
            </div>
          </div>

          <div data-testid="floor-tap" className="absolute inset-0 z-[15] cursor-pointer" onClick={tapToWalk} />

          {/* soft rug under the board table */}
          <div className="pointer-events-none absolute z-[16] h-16 w-40 rounded-[50%] bg-[#b3141f]/25 blur-sm sm:h-20 sm:w-52" style={{ left: "72%", top: "70%", transform: "translate(-50%,-50%)" }} />

          {ZONES.map((z) => (
            <div key={z.id} className="absolute z-20" style={{ left: `${z.x}%`, top: `${z.y}%`, transform: "translate(-50%,-50%)" }}>
              {active?.id === z.id && <InteractPrompt />}
              <button data-testid={`zone-${z.id}`} onClick={() => interact(z)}
                className={`group relative block rounded-lg transition-transform hover:scale-[1.05] ${active?.id === z.id ? "zone-glow scale-[1.05]" : ""}`}>
                {z.kind === "arcade" && <ArcadeCabinet />}
                {z.kind === "board" && <BoardTable />}
                {z.kind === "home" && <ExitDoor />}
                {z.kind === "board" && <span className="absolute -right-2 -top-2 z-10 rounded-full border-2 border-kirana-brown bg-kirana-gold px-1.5 text-[8px] font-bold text-kirana-brown">NEW</span>}
              </button>
            </div>
          ))}

          <Avatar pos={pos} facing={facing} moving={moving} />
        </div>

        {/* touch controls — below the scene so they never cover a card */}
        <div className={`touch-pad mx-auto mt-3 w-max grid-cols-3 gap-1.5 ${isTouch ? "grid" : "hidden"}`}>
          <span />
          <DPad onDown={() => startHold(0, -1.5)} onUp={stopHold} icon={ArrowUp} tid="dpad-up" />
          <span />
          <DPad onDown={() => startHold(-1.5, 0)} onUp={stopHold} icon={ArrowLeft} tid="dpad-left" />
          <DPad onDown={() => active && interact(active)} onUp={() => {}} label="⏎" tid="dpad-enter" />
          <DPad onDown={() => startHold(1.5, 0)} onUp={stopHold} icon={ArrowRight} tid="dpad-right" />
          <span />
          <DPad onDown={() => startHold(0, 1.5)} onUp={stopHold} icon={ArrowDown} tid="dpad-down" />
          <span />
        </div>
      </main>

      {arcadeOpen && <GameArcade open={arcadeOpen} onClose={() => setArcadeOpen(false)} groups={["Arcade"]} title="Video Game Parlour" subtitle="8-bit classics — tap a game to play" />}
      {boardOpen && <GameArcade open={boardOpen} onClose={() => setBoardOpen(false)} groups={["Board"]} title="Board Games" subtitle="Desi classics vs the computer — tap a game to play" />}
    </div>
  );
}

const DPad = ({ onDown, onUp, icon: Icon, label, tid }) => (
  <button data-testid={tid} onPointerDown={(e) => { e.preventDefault(); onDown(); }} onPointerUp={onUp} onPointerLeave={onUp} onContextMenu={(e) => e.preventDefault()}
    className="flex h-11 w-11 items-center justify-center rounded-md border-2 border-kirana-brown bg-kirana-cream/90 text-kirana-brown hard-shadow-sm">
    {Icon ? <Icon className="h-5 w-5" strokeWidth={3} /> : <span className="font-bold">{label}</span>}
  </button>
);

const ArcadeCabinet = () => (
  <div className="flex w-36 flex-col items-center sm:w-52">
    {/* marquee */}
    <div className="w-[86%] rounded-t-xl border-[3px] border-black bg-gradient-to-r from-[#ff2d95] via-[#ffb703] to-[#7C3AED] px-2 py-1 text-center hard-shadow-sm">
      <span className="font-signboard text-[10px] font-bold tracking-widest text-white sm:text-sm">GAME ZONE</span>
    </div>
    {/* body */}
    <div className="w-full rounded-t-lg border-[3px] border-black bg-gradient-to-b from-[#3a2560] to-[#2b1a4d] px-2 pb-1 pt-2">
      {/* screen */}
      <div className="rounded-md border-[3px] border-black bg-black p-1">
        <div className="crt-scanlines flex aspect-[4/3] flex-col items-center justify-center rounded bg-gradient-to-b from-[#12122a] to-[#05050f]">
          <Joystick className="h-7 w-7 text-[#a78bfa] drop-shadow sm:h-9 sm:w-9" strokeWidth={2} />
          <span className="mt-1 font-signboard text-[9px] font-bold text-[#22d3ee] sm:text-xs">▸ PLAY ◂</span>
        </div>
      </div>
      {/* control deck */}
      <div className="mt-2 flex items-center justify-between rounded bg-black/40 px-2 py-1.5">
        <span className="h-3.5 w-3.5 rounded-full bg-[#8a5a2b] ring-2 ring-black sm:h-4 sm:w-4" />
        <div className="flex gap-1">
          <span className="h-2.5 w-2.5 rounded-full bg-kirana-red sm:h-3 sm:w-3" />
          <span className="h-2.5 w-2.5 rounded-full bg-kirana-gold sm:h-3 sm:w-3" />
          <span className="h-2.5 w-2.5 rounded-full bg-kirana-green sm:h-3 sm:w-3" />
        </div>
      </div>
    </div>
    {/* base */}
    <div className="h-2 w-[94%] rounded-b-md bg-black sm:h-3" />
    <span className="mt-1 font-signboard text-[10px] font-bold text-white drop-shadow sm:text-sm">Arcade Console</span>
  </div>
);

const BoardTable = () => (
  <div className="flex w-32 flex-col items-center sm:w-48">
    {/* tabletop */}
    <div className="w-full rounded-xl border-[4px] border-[#6b3f1d] bg-gradient-to-b from-[#c99a5b] to-[#a97a3d] p-2 hard-shadow">
      <div className="rounded-md border-[3px] border-[#6b3f1d] bg-[#e7b96a] p-1">
        <div className="relative aspect-square rounded border-2 border-[#b3141f]">
          <span className="absolute -left-0.5 -top-0.5 h-3 w-3 rounded-full bg-[#20130a]" />
          <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-[#20130a]" />
          <span className="absolute -bottom-0.5 -left-0.5 h-3 w-3 rounded-full bg-[#20130a]" />
          <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-[#20130a]" />
          <span className="absolute left-1/2 top-1/2 h-9 w-9 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#b3141f] sm:h-11 sm:w-11" />
          <Dices className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 text-[#6b3f1d] sm:h-7 sm:w-7" strokeWidth={2} />
          <span className="absolute left-[28%] top-[30%] h-2.5 w-2.5 rounded-full bg-white ring-1 ring-black/40" />
          <span className="absolute right-[28%] top-[32%] h-2.5 w-2.5 rounded-full bg-[#241a12]" />
          <span className="absolute bottom-[28%] left-[34%] h-2.5 w-2.5 rounded-full bg-[#c1121f]" />
          <span className="absolute bottom-[30%] right-[30%] h-2.5 w-2.5 rounded-full bg-white ring-1 ring-black/40" />
        </div>
      </div>
    </div>
    {/* legs */}
    <div className="flex w-[78%] justify-between">
      <span className="h-4 w-2 rounded-b bg-[#6b3f1d] sm:h-6" />
      <span className="h-4 w-2 rounded-b bg-[#6b3f1d] sm:h-6" />
    </div>
    <span className="mt-1 font-signboard text-[10px] font-bold text-white drop-shadow sm:text-sm">Board Games</span>
  </div>
);

const ExitDoor = () => (
  <div className="flex flex-col items-center">
    <div className="flex h-16 w-12 items-center justify-center rounded-t-full border-[3px] border-kirana-brown bg-gradient-to-b from-[#8a5a2b] to-[#6D4C41] hard-shadow-sm sm:h-24 sm:w-16">
      <HomeIcon className="h-5 w-5 text-kirana-cream sm:h-7 sm:w-7" />
    </div>
    <span className="mt-1 font-signboard text-[10px] font-bold text-white drop-shadow sm:text-sm">Nostalgic World</span>
  </div>
);
